import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useCanvasStore, Item, Board } from '@/stores/canvasStore';
import { WallItemCard } from './WallItemCard';
import { WallFilterBar } from './WallFilterBar';
import { WallSearchWithFilters } from './WallSearchWithFilters';
import { PinnedSection } from './PinnedSection';
import { QuickAddNoteCard } from './QuickAddNoteCard';
import { WallPreviewModal } from './WallPreviewModal';
import { PartnrConfirmDialog } from '@/components/partnr/PartnrConfirmDialog';
import { DropZone } from '@/components/upload/DropZone';
import { Loader2 } from 'lucide-react';
import { useWallPaste } from '@/hooks/useWallPaste';
import { cn } from '@/lib/utils';
import { distributeColumns, useColumnCount } from '@/lib/masonryColumns';
import { useProgressiveCount } from '@/hooks/useProgressiveCount';

// Helper to extract searchable text from an item
const getSearchableText = (item: Item): string => {
  const parts: string[] = [];
  const content = item.content as Record<string, unknown>;
  
  // Common fields
  if (content.filename) parts.push(content.filename as string);
  if (content.title) parts.push(content.title as string);
  if (content.description) parts.push(content.description as string);
  
  // AI-generated metadata
  if (content.summary) parts.push(content.summary as string);
  if (content.analysis) parts.push(content.analysis as string);
  if (content.transcript) parts.push(content.transcript as string);
  if (content.searchText) parts.push(content.searchText as string);
  if (content.alt) parts.push(content.alt as string);
  
  // Type-specific content
  switch (item.item_type) {
    case 'sticky':
      if (content.text) parts.push(content.text as string);
      break;
    case 'text_card':
      if (content.body) parts.push(content.body as string);
      break;
    case 'document':
      if (content.content) {
        // Strip HTML tags for search
        const textContent = (content.content as string).replace(/<[^>]*>/g, '');
        parts.push(textContent);
      }
      break;
    case 'link':
      if (content.url) parts.push(content.url as string);
      if (content.siteName) parts.push(content.siteName as string);
      break;
    case 'scraped_creative':
      for (const key of ['account_handle', 'account_name', 'caption', 'hook_text', 'content_url', 'platform']) {
        if (typeof content[key] === 'string') parts.push(content[key] as string);
      }
      break;
  }
  
  return parts.join(' ');
};

export function WallView() {
  const { allItems, boards, isLoading, fetchAllItems, fetchBoards, updateItem, setWallScrollOffset, getVisibleBoards } = useCanvasStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [selectedBoardIds, setSelectedBoardIds] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showBoardFilters, setShowBoardFilters] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAllItems();
    fetchBoards();
  }, [fetchAllItems, fetchBoards]);

  // Track scroll position for TopBar animation
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      setWallScrollOffset(scrollContainerRef.current.scrollTop);
    }
  }, [setWallScrollOffset]);

  // Reset scroll offset when unmounting
  useEffect(() => {
    return () => setWallScrollOffset(0);
  }, [setWallScrollOffset]);

  const columnCount = useColumnCount();

  // Derive pinned items reactively from allItems for instant UI updates
  const pinnedItems = useMemo(() => 
    allItems
      .filter(item => item.is_pinned_wall === true)
      .sort((a, b) => 
        new Date(b.pinned_wall_at || 0).getTime() - 
        new Date(a.pinned_wall_at || 0).getTime()
      ),
    [allItems]
  );

  // Refresh items after file upload
  const handleFilesUploaded = () => {
    fetchAllItems();
  };

  // Get target board for uploads - use first selected filter or null (no board)
  const getTargetBoardId = (): string | null => {
    if (selectedBoardIds.size > 0) {
      return Array.from(selectedBoardIds)[0];
    }
    // Return null - notes created in Wall don't need a board
    return null;
  };

  // Paste handler
  useWallPaste({
    targetBoardId: getTargetBoardId(),
    onItemCreated: fetchAllItems,
  });

  // Handle pin/unpin with optimistic updates via store
  const handlePinItem = async (itemId: string) => {
    await updateItem(itemId, { 
      is_pinned_wall: true, 
      pinned_wall_at: new Date().toISOString() 
    });
  };

  const handleUnpinItem = async (itemId: string) => {
    await updateItem(itemId, { 
      is_pinned_wall: false, 
      pinned_wall_at: null 
    });
  };

  // FIX 5: Filter out pinned items from main grid with scored search
  const filteredItems = useMemo(() => {
    // Only show links, images, and videos in discover view (exclude text cards, stickies, documents, files)
    // scraped_creative: a pasted post pulled with Top Creatives is upgraded in place and must stay in Discover
    const DISCOVER_TYPES = new Set(['link', 'image', 'video', 'scraped_creative']);
    let items = allItems.filter(item =>
      DISCOVER_TYPES.has(item.item_type) &&
      item.is_pinned_wall !== true
    );

    // Filter by selected boards
    if (selectedBoardIds.size > 0) {
      items = items.filter(item => selectedBoardIds.has(item.board_id));
    }

    // Filter by type filters
    if (typeFilters.length > 0) {
      items = items.filter(item => {
        // Special handling for videos (stored as 'video' type)
        if (typeFilters.includes('video') && item.item_type === 'video') {
          return true;
        }
        // Top Creatives cards are pulled links
        if (typeFilters.includes('link') && item.item_type === 'scraped_creative') {
          return true;
        }
        return typeFilters.includes(item.item_type);
      });
    }

    // Filter by search query with scoring
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const queryWords = q.split(/\s+/).filter(w => w.length > 1);
      
      const scored = items.map(item => {
        const searchableText = getSearchableText(item).toLowerCase();
        let score = 0;

        // Exact phrase match
        if (searchableText.includes(q)) {
          score += 100;
        }

        // Individual word matches
        for (const word of queryWords) {
          if (searchableText.includes(word)) {
            score += 10;
          }
          // Word-start matches (e.g., "des" matches "design")
          const wordStartRegex = new RegExp(`\\b${word}`, 'i');
          if (wordStartRegex.test(searchableText)) {
            score += 5;
          }
        }

        // Recency boost
        const ageMs = Date.now() - new Date(item.created_at).getTime();
        const ageDays = ageMs / (1000 * 60 * 60 * 24);
        if (ageDays < 7) score += 3;
        if (ageDays < 1) score += 5;

        return { item, score };
      });

      items = scored
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(s => s.item);
    } else {
      // Sort by most recent when no search
      items = items.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }

    return items;
  }, [allItems, selectedBoardIds, searchQuery, typeFilters]);

  // Cards render a screenful at a time as you scroll (an account can hold
  // thousands); a new search or filter starts again from the top
  const pagingKey = `${searchQuery}\u0000${typeFilters.join(',')}\u0000${[...selectedBoardIds].join(',')}`;
  const { count: shownCount, hasMore, sentinelRef } = useProgressiveCount(filteredItems.length, pagingKey, scrollContainerRef);
  const shownItems = useMemo(() => filteredItems.slice(0, shownCount), [filteredItems, shownCount]);
  // The search bar is sticky, so a search can start deep in the wall: show its results from the top
  const lastPagingKeyRef = useRef(pagingKey);
  useEffect(() => {
    if (lastPagingKeyRef.current === pagingKey) return;
    lastPagingKeyRef.current = pagingKey;
    const el = scrollContainerRef.current;
    if (el && el.scrollTop > 0) el.scrollTo({ top: 0, behavior: 'auto' });
  }, [pagingKey]);

  const toggleBoardFilter = (boardId: string) => {
    setSelectedBoardIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(boardId)) {
        newSet.delete(boardId);
      } else {
        newSet.add(boardId);
      }
      return newSet;
    });
  };

  const getBoardForItem = (item: Item): Board | undefined => {
    return boards.find(b => b.id === item.board_id);
  };

  const handleItemUpdate = () => {
    fetchAllItems();
  };

  if (isLoading && allItems.length === 0) {
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-ink-3">Loading your mind...</p>
      </div>
    );
  }

  const hasActiveSearch = searchQuery || typeFilters.length > 0;

  return (
    <DropZone mode="wall" onFilesUploaded={handleFilesUploaded}>
      <motion.div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="min-h-screen h-screen overflow-y-auto scroll-smooth bg-background text-foreground"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {/* Search Section with Filters */}
        <WallSearchWithFilters
          value={searchQuery} 
          onChange={setSearchQuery}
          activeFilters={typeFilters}
          onFiltersChange={setTypeFilters}
          resultCount={hasActiveSearch ? filteredItems.length : undefined}
          showBoardFilters={showBoardFilters}
          onToggleBoardFilters={() => setShowBoardFilters(!showBoardFilters)}
        />

        {/* Board Tag Filters - only visible when board filters toggle is enabled */}
        {/* Use all boards (not getVisibleBoards) so users can filter by Home board too */}
        <WallFilterBar
          boards={boards}
          selectedBoardIds={selectedBoardIds}
          onToggleBoardFilter={toggleBoardFilter}
          isVisible={showBoardFilters}
        />

        {/* Pinned Section */}
        <PinnedSection
          pinnedItems={pinnedItems}
          boards={boards}
          onUnpin={handleUnpinItem}
          onItemClick={setSelectedItem}
        />

        {/* Masonry Grid */}
        <div className="max-w-7xl mx-auto px-6 pb-24">
          {filteredItems.length === 0 && !hasActiveSearch ? (
            <div className="flex flex-col items-center justify-center h-64 text-ink-3">
              <p className="text-lg mb-2">Your mind is empty</p>
              <p className="text-sm">Start adding notes, images, and links</p>
            </div>
          ) : filteredItems.length === 0 && hasActiveSearch ? (
            <div className="flex flex-col items-center justify-center h-64 text-ink-3">
              <p className="text-lg mb-2">No results found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            // Columns are dealt left to right so the newest items run across the top,
            // so rendering more of the list only adds to the bottom of each column
            <div className="flex items-start gap-5">
              {distributeColumns<Item | 'quick-add'>(['quick-add', ...shownItems], columnCount).map((column, c) => (
                <div key={c} className="flex min-w-0 flex-1 flex-col gap-5">
                  {column.map((entry) =>
                    entry === 'quick-add' ? (
                      // Quick Add Note Card - always first
                      <QuickAddNoteCard key="quick-add" targetBoardId={null} onNoteCreated={fetchAllItems} />
                    ) : (
                      <WallItemCard
                        key={entry.id}
                        item={entry}
                        board={getBoardForItem(entry)}
                        boards={boards}
                        onClick={() => setSelectedItem(entry)}
                        onPin={() => handlePinItem(entry.id)}
                        onDelete={handleItemUpdate}
                        isPinned={pinnedItems.some(p => p.id === entry.id)}
                      />
                    ),
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Comes within reach of the bottom ahead of the reader: the next rows render before they are needed */}
          {hasMore && <div ref={sentinelRef} aria-hidden className="h-px" />}
        </div>

        {/* Preview Modal with inline editing */}
        {selectedItem && (
          <WallPreviewModal
            item={selectedItem}
            board={getBoardForItem(selectedItem)}
            onClose={() => setSelectedItem(null)}
            onUpdate={handleItemUpdate}
          />
        )}

        {/* Delete confirms (deleteDiscoverItem); the canvas mounts its own, and only one view shows at a time */}
        <PartnrConfirmDialog />
      </motion.div>
    </DropZone>
  );
}
